import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../../../../context/auth-context";

/** ---------------------- Tipos da API nova ---------------------- */

export type StandbyItemAPI = {
  id: number | string;
  movimentacao_id: number | string;
  produto_id: number | string;
  estoque_id?: number | string | null;

  quantidade: number | string;
  quantidade_origem?: number | string | null;
  quantidade_retornada?: number | string | null;
  quantidade_baixada?: number | string | null;

  custo_unitario: string | number | null;
  custo_unitario_atual?: string | number | null;
  margem_percentual?: string | number | null;
  preco_venda?: string | number | null;
  moeda?: string | null;

  saida_mov_ids?: Array<number | string>;
  retorno_mov_ids?: Array<number | string>;

  pendente_retorno?: boolean;
  pendente_responsavel_id?: number | string | null;
  pendente_responsavel_nome?: string | null;
  pendente_criado_em?: string | null;

  produto: {
    id: number | string;
    codigo: string;
    nome: string;
    posicao: string | null;
    ncm?: string | null;
    classificacao?: string | null;
    peso?: string | number | null;
    dimensao?: string | null;
    ocultar?: boolean | null;
  };
};

export type StandbyMovAPI = {
  movimentacao: {
    id: number | string;
    empresa_id?: number | string | null;
    estoque_id?: number | string | null;
    tipo_movimentacao_id?: number | string | null;
    codigo_movimentacao?: string | null;

    tipo: string | null; // standby-servico | standby-venda
    tipo_standby?: "servico" | "venda" | string | null;

    responsavel_id: number | string | null;
    responsavel_nome: string | null;

    criado_em: string;
    os_numero?: string | null;
    proposta_venda?: string | null;
    coletor_nome?: string | null;
    embarcacao?: string | null;
    cliente_nome?: string | null;
    empresa_cliente?: string | null;

    custo_total?: string | number | null;
    motivo?: string | null;
    origem_mov_id?: number | string | null;
    coordenador_responsavel_ids?: unknown;

    comentario?: string | null;

    pendente_retorno?: boolean;
    pendente_id?: number | string | null;
    pendente_responsavel_id?: number | string | null;
    pendente_responsavel_nome?: string | null;
    pendente_criado_em?: string | null;

    empresa?: {
      id?: number | string | null;
      nome_fantasia?: string | null;
      responsaveis_ids?: Array<number | string>;
      responsaveis_nomes?: string[];
    };

    estoque?: {
      id?: number | string | null;
      codigo?: string | null;
      nome?: string | null;
      modulo_id?: number | string | null;
    };

    modulo?: {
      id?: number | string | null;
      codigo?: string | null;
      label?: string | null;
    };
  };
  itens: StandbyItemAPI[];
};

type RespAPI = {
  ok: boolean;
  fonte?: "cache" | "banco";
  estoque?: {
    id?: number | string;
    codigo?: string;
    nome?: string;
    modulo_id?: number | string;
  };
  modulo?: {
    id?: number | string;
    codigo?: string;
    label?: string;
  };
  meta: {
    filtros: {
      tipo_standby?: "servico" | "venda" | string | null;
      date_from: string | null;
      date_to: string | null;
      order: "ASC" | "DESC" | "asc" | "desc";
      limit: number;
      offset: number;
    };
    count: number;
    total?: number;
    has_more?: boolean;
  };
  data: StandbyMovAPI[];
};

type ModuloAtual = {
  id: number;
  codigo: string;
  label: string;
};

/** ---------------------- Constantes ---------------------- */

const URL_STANDBY =
  "https://api.logvi-havox.com/template/estoque-administrativo/get-standby";

const POLL_FALLBACK_MS = 15000;
const ACCESS_TOKEN_KEY = "access_token";

/** ---------------------- Utils ---------------------- */

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const jitter = (ms: number) => ms + Math.floor(Math.random() * 400);

function normalizeComparable(value: unknown): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/_/g, "-")
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function getAccessToken(): string | null {
  try {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  } catch {
    return null;
  }
}

function buildAuthHeaders(includeContentType = false): Record<string, string> {
  const token = getAccessToken();

  const headers: Record<string, string> = {
    Accept: "application/json",
  };

  if (includeContentType) {
    headers["Content-Type"] = "application/json";
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

function toBRT(iso: string): { data: string; hora: string } {
  if (!iso) return { data: "-", hora: "-" };

  try {
    const d = new Date(iso);

    if (Number.isNaN(d.getTime())) {
      return {
        data: iso,
        hora: "",
      };
    }

    const data = d.toLocaleDateString("pt-BR", {
      timeZone: "America/Sao_Paulo",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

    const hora = d.toLocaleTimeString("pt-BR", {
      timeZone: "America/Sao_Paulo",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    return { data, hora };
  } catch {
    return { data: iso, hora: "" };
  }
}

function parseNumber(value: unknown): number {
  const n =
    typeof value === "number"
      ? value
      : Number(String(value ?? "0").replace(",", "."));

  return Number.isFinite(n) ? n : 0;
}

function formatBRL(x: number): string {
  return x.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatMoney(valor: number, moeda?: string | null): string {
  const currency = String(moeda || "BRL").toUpperCase();

  if (currency === "BRL") return `R$ ${formatBRL(valor)}`;

  const symbol = currency === "USD" ? "$" : currency === "EUR" ? "€" : currency;

  return `${symbol} ${valor.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function tipoLabel(tipo: string | null | undefined): string {
  const v = String(tipo || "").toLowerCase();

  if (!v) return "-";
  if (v.includes("servico") || v.includes("serviço")) return "Serviço";
  if (v.includes("venda")) return "Venda";

  return String(tipo);
}

function getPropostaOuOS(mov: StandbyMovAPI["movimentacao"]): string {
  const proposta = String(mov.proposta_venda ?? "").trim();
  const os = String(mov.os_numero ?? "").trim();

  if (proposta && proposta.toLowerCase() !== "n/a") return proposta;
  if (os && os.toLowerCase() !== "n/a") return os;

  return "-";
}

function getPathSegments(pathname: string): string[] {
  const pathWithoutQuery = String(pathname || "")
    .split("?")[0]
    .split("#")[0];

  return pathWithoutQuery
    .split("/")
    .filter(Boolean)
    .map((segment) => normalizeComparable(segment));
}

function findModuloAtualFromSessao(
  sessao: any,
  pathname: string
): ModuloAtual | null {
  /*
   * Exemplo:
   * /comercial/materiais-p-venda/standby
   *
   * areaSlug   = comercial
   * moduloSlug = materiais-p-venda
   * submodulo  = standby
   */
  const [areaSlug, moduloSlug] = getPathSegments(pathname);

  if (!areaSlug || !moduloSlug) {
    return null;
  }

  const enderecos = Array.isArray(sessao?.enderecos_com_modulos)
    ? sessao.enderecos_com_modulos
    : [];

  if (!enderecos.length) {
    return null;
  }

  const enderecoSelecionadoId =
    sessao?.endereco_selecionado_id !== undefined &&
    sessao?.endereco_selecionado_id !== null
      ? String(sessao.endereco_selecionado_id)
      : null;

  /*
   * Usa exclusivamente o endereço selecionado.
   * Não deve procurar silenciosamente em outro endereço,
   * pois isso poderia usar módulos de uma unidade diferente.
   */
  const enderecoSelecionado = enderecoSelecionadoId
    ? enderecos.find(
        (endereco: any) => String(endereco?.id) === enderecoSelecionadoId
      )
    : enderecos[0];

  if (!enderecoSelecionado) {
    return null;
  }

  const departamentos = Array.isArray(
    enderecoSelecionado?.departamentos_acessiveis
  )
    ? enderecoSelecionado.departamentos_acessiveis
    : [];

  const departamentoAtual = departamentos.find((departamento: any) => {
    const identificadores = [
      departamento?.routeKey,
      departamento?.route_key,
      departamento?.slug,
      departamento?.nome,
    ]
      .map((value) => normalizeComparable(value))
      .filter(Boolean);

    return identificadores.includes(areaSlug);
  });

  if (!departamentoAtual) {
    return null;
  }

  const modulos = Array.isArray(departamentoAtual?.modulos)
    ? departamentoAtual.modulos
    : [];

  const moduloEncontrado = modulos.find((modulo: any) => {
    /*
     * Uma permissão explicitamente falsa deve impedir o uso.
     * Ausência da propriedade mantém compatibilidade com sessões antigas.
     */
    if (modulo?.permissions?.canView === false) {
      return false;
    }

    const identificadores = [
      modulo?.codigo,
      modulo?.label,
      modulo?.routeBase,
      modulo?.rota_base,
    ]
      .map((value) => normalizeComparable(value))
      .filter(Boolean);

    return identificadores.includes(moduloSlug);
  });

  if (moduloEncontrado?.id === undefined || moduloEncontrado?.id === null) {
    return null;
  }

  const moduloId = Number(moduloEncontrado.id);

  if (!Number.isInteger(moduloId) || moduloId <= 0) {
    return null;
  }

  return {
    id: moduloId,
    codigo: String(moduloEncontrado.codigo || ""),
    label: String(moduloEncontrado.label || ""),
  };
}

/** ---------------------- Linha da tabela ---------------------- */

/**
 * Ordem compatível com o hook antigo:
 * [
 *   Proposta/OS,
 *   Tipo,
 *   Código,
 *   Nome do Produto,
 *   Quantidade,
 *   Custo Unitário,
 *   Técnico,
 *   Data,
 *   Hora
 * ]
 */
export type StandbyRowUI = [
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string
];

function flattenToRows(movs: StandbyMovAPI[]): StandbyRowUI[] {
  const rows: StandbyRowUI[] = [];

  for (const m of movs || []) {
    const { data, hora } = toBRT(m.movimentacao.criado_em);

    const propostaOuOs = getPropostaOuOS(m.movimentacao);
    const tipo = tipoLabel(m.movimentacao.tipo || m.movimentacao.tipo_standby);
    const tecnico =
      m.movimentacao.coletor_nome || m.movimentacao.responsavel_nome || "-";

    for (const it of m.itens || []) {
      const qtd = parseNumber(it.quantidade);
      const custo = parseNumber(it.custo_unitario);
      const moeda = it.moeda || "BRL";

      rows.push([
        propostaOuOs,
        tipo,
        String(it.produto?.codigo ?? "").toUpperCase(),
        String(it.produto?.nome ?? "").toUpperCase(),
        String(qtd),
        formatMoney(custo, moeda),
        tecnico,
        data,
        hora,
      ]);
    }
  }

  return rows;
}

function buildRawByProposta(rawMovs: StandbyMovAPI[]) {
  const index: Record<
    string,
    {
      mov: StandbyMovAPI["movimentacao"];
      itens: StandbyItemAPI[];
    }
  > = {};

  for (const m of rawMovs || []) {
    const proposta = getPropostaOuOS(m.movimentacao);

    if (!index[proposta]) {
      index[proposta] = {
        mov: m.movimentacao,
        itens: [],
      };
    }

    index[proposta].itens.push(...(m.itens || []));
  }

  return index;
}

/** ---------------------- Fetchers ---------------------- */

export type StandbyQuery = Partial<{
  tipo_standby: "servico" | "venda";
  date_from: string;
  date_to: string;
  order: "asc" | "desc";
  limit: number;
  offset: number;
}>;

export async function fetchStandbyOnce(
  moduloAtual: ModuloAtual | null,
  query?: StandbyQuery
): Promise<{
  rows: StandbyRowUI[];
  meta: RespAPI["meta"] | null;
  rawMovs: StandbyMovAPI[];
  rawByProposta: Record<
    string,
    { mov: StandbyMovAPI["movimentacao"]; itens: StandbyItemAPI[] }
  >;
  resp: RespAPI | null;
}> {
  if (!moduloAtual?.id) {
    throw new Error("Módulo de estoque não identificado para esta rota");
  }

  const payload = {
    modulo_id: moduloAtual.id,

    /**
     * Para o antigo useStandbyComercial, o padrão correto é venda.
     * Caso queira reaproveitar este hook para standby de serviço,
     * chame useStandbyComercial({ tipo_standby: "servico" }).
     */
    tipo_standby: query?.tipo_standby ?? "venda",

    order: query?.order ?? "desc",
    limit: query?.limit ?? 10000,
    offset: query?.offset ?? 0,
    ...(query?.date_from ? { date_from: query.date_from } : {}),
    ...(query?.date_to ? { date_to: query.date_to } : {}),
  };

  const res = await fetch(URL_STANDBY, {
    method: "POST",
    headers: buildAuthHeaders(true),
    cache: "no-store",
    body: JSON.stringify(payload),
  });

  const json = (await res.json().catch(() => null)) as RespAPI | null;

  if (!res.ok || json?.ok === false) {
    const erro =
      (json as any)?.erro ||
      (Array.isArray((json as any)?.erros) &&
        (json as any).erros.join(" | ")) ||
      `HTTP ${res.status}`;

    throw new Error(erro);
  }

  const rawMovs: StandbyMovAPI[] =
    json && Array.isArray(json.data) ? json.data : [];

  const rawByProposta = buildRawByProposta(rawMovs);

  return {
    rows: flattenToRows(rawMovs),
    meta: json?.meta ?? null,
    rawMovs,
    rawByProposta,
    resp: json,
  };
}

/** ---------------------- Hook ---------------------- */

export function useStandbyComercial(query?: StandbyQuery) {
  const location = useLocation();
  const { sessao, loading: authLoading } = useAuth();

  const moduloAtual = useMemo(
    () => findModuloAtualFromSessao(sessao, location.pathname),
    [sessao, location.pathname]
  );

  const [rows, setRows] = useState<StandbyRowUI[]>([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState<RespAPI["meta"] | null>(null);
  const [resp, setResp] = useState<RespAPI | null>(null);
  const [rawMovs, setRawMovs] = useState<StandbyMovAPI[]>([]);
  const [rawByProposta, setRawByProposta] = useState<
    Record<
      string,
      {
        mov: StandbyMovAPI["movimentacao"];
        itens: StandbyItemAPI[];
      }
    >
  >({});
  const [erro, setErro] = useState<string | null>(null);

  const stop = useRef(false);
  const fetching = useRef(false);

  async function carregar() {
    fetching.current = true;

    try {
      setErro(null);

      const result = await fetchStandbyOnce(moduloAtual, query);

      setRows(result.rows);
      setMeta(result.meta);
      setResp(result.resp);
      setRawMovs(result.rawMovs);
      setRawByProposta(result.rawByProposta);
    } catch (e: any) {
      if (e?.name !== "AbortError") {
        setErro(e?.message || "Erro ao carregar standby.");
        throw e;
      }
    } finally {
      fetching.current = false;
    }
  }

  useEffect(() => {
    stop.current = false;

    setLoading(true);
    setErro(null);
    setRows([]);
    setMeta(null);
    setResp(null);
    setRawMovs([]);
    setRawByProposta({});

    if (authLoading) {
      return () => {
        stop.current = true;
      };
    }

    if (!moduloAtual?.id) {
      setLoading(false);
      setErro("Módulo de estoque não identificado para esta rota.");
      return () => {
        stop.current = true;
      };
    }

    (async () => {
      try {
        await carregar();
        setLoading(false);

        while (!stop.current) {
          while (fetching.current && !stop.current) {
            await sleep(25);
          }

          if (stop.current) break;

          await sleep(jitter(POLL_FALLBACK_MS));

          if (stop.current) break;

          await carregar();
        }
      } catch (e) {
        if (!stop.current) {
          console.error("useStandbyComercial loop error:", e);
        }

        setLoading(false);
      }
    })();

    return () => {
      stop.current = true;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduloAtual?.id, authLoading, JSON.stringify(query)]);

  const refresh = async () => {
    while (fetching.current) {
      await sleep(25);
    }

    await carregar();
  };

  return {
    rows,
    loading: authLoading || loading,
    refresh,
    meta,
    resp,
    rawMovs,
    rawByProposta,
    erro,
    moduloAtual,
  };
}
